// 사용자 앱 루트 라우터 셸.
// 메인 레이아웃(하단 탭바 노출) 라우트와 인증 흐름 라우트(탭바 없음) 를 분리한다.
// 카탈로그 상세/검색 라우트도 메인 레이아웃 하위에 두어 탭바 일관성 유지.

import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "./shared/MainLayout.tsx";
import SplashScreen from "./shared/SplashScreen.tsx";
import BackButtonController from "./shared/BackButtonController.tsx";
import NativeBridge from "./shared/NativeBridge.tsx";
import NativeUpdatePrompt from "./features/appUpdate/NativeUpdatePrompt.tsx";
import ProtectedRoute from "./features/auth/ProtectedRoute.tsx";
import HomePage from "./pages/HomePage.tsx";
import CategoryPage from "./pages/CategoryPage.tsx";
import CartPage from "./pages/CartPage.tsx";
import MyPage from "./pages/MyPage.tsx";
import LoginPage from "./pages/auth/LoginPage.tsx";
import PhoneLoginPage from "./pages/auth/PhoneLoginPage.tsx";
import OtpPage from "./pages/auth/OtpPage.tsx";
import ConsentsPage from "./pages/auth/ConsentsPage.tsx";
import SocialCallbackPage from "./pages/auth/SocialCallbackPage.tsx";
import CategoryProductsPage from "./pages/catalog/CategoryProductsPage.tsx";
import SearchPage from "./pages/catalog/SearchPage.tsx";
import ProductDetailPage from "./pages/catalog/ProductDetailPage.tsx";
import AddressesPage from "./pages/account/AddressesPage.tsx";
import AddressFormPage from "./pages/account/AddressFormPage.tsx";
import CheckoutPage from "./pages/CheckoutPage.tsx";
import OrdersPage from "./pages/account/OrdersPage.tsx";
import OrderDetailPage from "./pages/account/OrderDetailPage.tsx";
import PointsPage from "./pages/account/PointsPage.tsx";
import CouponsPage from "./pages/account/CouponsPage.tsx";
import NotificationsPage from "./pages/account/NotificationsPage.tsx";
import SupportPage from "./pages/account/SupportPage.tsx";
import TermsPage from "./pages/legal/TermsPage.tsx";
import PrivacyPage from "./pages/legal/PrivacyPage.tsx";
import RefundPage from "./pages/legal/RefundPage.tsx";
import BusinessInfoPage from "./pages/legal/BusinessInfoPage.tsx";
import GamesPage from "./pages/games/GamesPage.tsx";
// 열린목장(Phaser)은 무거우므로 lazy 로 코드분할(메인 번들 영향 0).
const RanchPage = lazy(() => import("./pages/games/RanchPage.tsx"));

// 사용자 앱 루트 컴포넌트.
// 라우트 레이아웃과 하단 탭바(메인/카테고리/장바구니/마이페이지) 진입점을 한 곳에서 관리한다.
export default function App() {
  return (
    <>
      {/* 첫 진입 스플래시(관리자 설정 가능). 콜드 스타트에 한 번 표시 후 사라진다. */}
      <SplashScreen />
      {/* 안드로이드 시스템 뒤로가기 제어(탭->메인 / 상세->이전 / 메인 두번->종료). */}
      <BackButtonController />
      {/* 네이티브(Capacitor) 전용: 스플래시 숨김 + 소셜 로그인 딥링크 복귀 처리(웹에선 no-op). */}
      <NativeBridge />
      {/* 선택 업데이트 안내. Capacitor 앱에서만 원격 최신 버전과 설치 버전을 비교한다. */}
      <NativeUpdatePrompt />
      {/* 상태바 뒤 배경 스트립: viewport-fit=cover 라 스크롤 컨텐츠가 상태바 밑을 지나가는데,
          이 불투명 스트립이 그 영역을 항상 페이지 배경색으로 덮어 상태바 아이콘 가독성을 지킨다.
          웹/데스크톱은 --safe-top=0 이라 높이 0(무해). 스플래시(z-9999)보다는 아래. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 z-[9990] bg-page"
        style={{ height: "var(--safe-top)" }}
      />
      <Routes>
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/category" element={<CategoryPage />} />
        <Route path="/category/:id" element={<CategoryProductsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/product/:id" element={<ProductDetailPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me"
          element={
            <ProtectedRoute>
              <MyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/orders"
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/points"
          element={
            <ProtectedRoute>
              <PointsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/coupons"
          element={
            <ProtectedRoute>
              <CouponsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/me/support" element={<SupportPage />} />
        {/* 법적 고지(공개): 사업자정보/이용약관/개인정보처리방침/취소·환불 정책. 비로그인도 접근. */}
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/refund" element={<RefundPage />} />
        <Route path="/legal/business" element={<BusinessInfoPage />} />
        <Route
          path="/me/games"
          element={
            <ProtectedRoute>
              <GamesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/orders/:id"
          element={
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/addresses"
          element={
            <ProtectedRoute>
              <AddressesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/addresses/new"
          element={
            <ProtectedRoute>
              <AddressFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/addresses/:id/edit"
          element={
            <ProtectedRoute>
              <AddressFormPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 열린목장 플레이는 탭바 없는 전체화면(MainLayout 밖). lazy 로딩 동안 빈 배경. */}
      <Route
        path="/me/games/ranch"
        element={
          <ProtectedRoute>
            <Suspense fallback={<div className="fixed inset-0 bg-[#bfe3f5]" />}>
              <RanchPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/phone" element={<PhoneLoginPage />} />
      <Route path="/login/otp" element={<OtpPage />} />
      <Route path="/login/consents" element={<ConsentsPage />} />
      <Route path="/auth/social-callback" element={<SocialCallbackPage />} />
      </Routes>
    </>
  );
}
